import { apiUtils } from '../hooks';
import { API_ENDPOINTS } from './apiEndpoints';
import { throwApiError } from '../utils/errorUtils';

/**
 * Service to manage Hotel Organizations
 *
 * This service provides methods to interact with the hotel organization API endpoints.
 * Methods include fetching, creating, updating, deleting hotel organizations,
 * and assigning hotels to organizations.
 */
class HotelOrgService {
  /**
   *
   * @param search
   * @returns
   */
  // Fetch list of hotel organizations
  async getHotelOrganizations(params: Record<string, any> = {}) {
    try {
      const response = await apiUtils.get(API_ENDPOINTS.hotelOrgs.list, params);
      return response.data;
    } catch (error) {
      throwApiError(error, 'Failed to fetch hotel organizations');
    }
  }
  /**
   *
   * @param id
   * @returns
   */
  // Get a single hotel organization by ID
  async getHotelOrganization(id: string) {
    try {
      const response = await apiUtils.get(
        `${API_ENDPOINTS.hotelOrgs.list}/${id}`
      );
      return response.data;
    } catch (error) {
      throwApiError(error, 'Failed to fetch hotel organization');
    }
  }
  /**
   *
   * @param data
   * @returns
   */
  // Create a new hotel organization
  async createHotelOrganization(data: {
    name: string;
    acquired_only: boolean;
  }) {
    try {
      const response = await apiUtils.post(
        API_ENDPOINTS.hotelOrgs.create,
        data
      );
      return response.data;
    } catch (error: unknown) {
      throwApiError(error, 'Failed to create hotel organization');
    }
  }
  /**
   *
   * @param id
   * @param data
   * @returns
   */
  // Update an existing hotel organization
  async updateHotelOrganization(id: string, data: { name: string }) {
    try {
      const response = await apiUtils.put(
        API_ENDPOINTS.hotelOrgs.update(id),
        data
      );
      return response.data;
    } catch (error) {
      throwApiError(error, 'Failed to update hotel organization');
    }
  }
  /**
   *
   * @param id
   * @returns
   */
  // Delete a hotel organization
  async deleteHotelOrganization(id: string) {
    try {
      const response = await apiUtils.delete(
        API_ENDPOINTS.hotelOrgs.delete(id)
      );
      return response.data;
    } catch (error) {
      throwApiError(error, 'Failed to delete hotel organization');
    }
  }
  /**
   *
   * @param id
   * @param hotelIds
   * @returns
   */
  // Assign hotels to a hotel organization
  async assignHotelsToOrganization(id: string, hotelIds: string[]) {
    try {
      const response = await apiUtils.post(API_ENDPOINTS.hotelOrgs.assign(id), {
        hotel_ids: hotelIds,
      });

      return response.data;
    } catch (error) {
      throwApiError(error, 'Failed to assign hotels to organization');
    }
  }

  // Remove hotels from a hotel organization
  /**
   *
   * @param id
   * @param hotelIds
   * @returns
   */
  async removeHotelsFromOrganization(id: string, hotelIds: string[]) {
    try {
      const response = await apiUtils.post(API_ENDPOINTS.hotelOrgs.remove(id), {
        hotel_ids: hotelIds,
      });

      return response.data;
    } catch (error) {
      throwApiError(error, 'Failed to remove hotels from organization');
    }
  }

  /**
   *
   * @param id
   * @param assigned
   * @param page
   * @param limit
   * @param searchTerm
   * @returns
   */
  async getHotelsByOrganization(
    id: string,
    assigned: boolean,
    page: number = 1,
    limit: number = 10,
    searchTerm: string = '',
    sortKey: string = '',
    sortOrder: 'asc' | 'desc' | null = null
  ) {
    try {
      if (assigned) {
        return await this.getAssignedHotels(
          id,
          page,
          limit,
          searchTerm,
          sortKey,
          sortOrder
        );
      }
      return await this.getUnAssignedHotels(
        id,
        page,
        limit,
        searchTerm,
        sortKey,
        sortOrder
      );
    } catch (error) {
      console.error('Failed to fetch hotels by organization', error);
    }
  }

  /**
   *
   * @param id
   * @param page
   * @param limit
   * @param searchTerm
   * @returns
   */
  async getAssignedHotels(
    id: string,
    page: number = 1,
    limit: number = 10,
    searchTerm: string = '',
    sortKey: string = '',
    sortOrder: 'asc' | 'desc' | null = null
  ) {
    try {
      const params: any = {
        page,
        limit,
        primarySortKey: sortKey,
        primarySortOrder: sortOrder,
      };
      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      const response = await apiUtils.get(
        API_ENDPOINTS.hotelOrgs.assignedHotels(id),
        { ...params }
      );

      return response.data;
    } catch (error) {
      throwApiError(error, 'Failed to fetch assigned hotels for organization');
    }
  }
  /**
   *
   * @param id
   * @param page
   * @param limit
   * @param searchTerm
   * @returns
   */
  async getUnAssignedHotels(
    id: string,
    page: number = 1,
    limit: number = 10,
    searchTerm: string = '',
    sortKey: string = '',
    sortOrder: 'asc' | 'desc' | null = null
  ) {
    try {
      const params: any = {
        page,
        limit,
        primarySortKey: sortKey,
        primarySortOrder: sortOrder,
      };
      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      const response = await apiUtils.get(
        API_ENDPOINTS.hotelOrgs.unassignedHotels(id),
        { ...params }
      );

      return response.data;
    } catch (error) {
      throwApiError(
        error,
        'Failed to fetch unassigned hotels for organization'
      );
    }
  }

  /**
   *
   * @param organization_id
   * @param new_org_id
   * @param hotelIds
   * @returns
   */
  async addToOtherHotelOrganization(
    organization_id: string,
    new_org_id: string,
    hotelIds: string[]
  ) {
    try {
      const response = await apiUtils.put(
        API_ENDPOINTS.hotelOrgs.moveToAnotherOrg(organization_id, new_org_id),
        { hotel_ids: hotelIds }
      );

      return response.data;
    } catch (error) {
      throwApiError(error, 'Failed to move hotels to another organization');
    }
  }

  // ORGANIZATION USERS METHODS BELOW
  /**
   * Get users by organization ID
   * @param id
   * @returns
   */
  async getUsersByOrganization(id: string) {
    try {
      const response = await apiUtils.get(
        API_ENDPOINTS.hotelOrgs.getLinkedUsers(id)
      );
      return response.data;
    } catch (error) {
      throwApiError(error, 'Failed to fetch users for organization');
    }
  }

  /**
   *
   * @param orgId
   * @param userData
   * @returns
   */
  async createNewUserInOrganization(
    orgId: string,
    userData: { email: string; role: string }
  ) {
    try {
      const response = await apiUtils.post(
        API_ENDPOINTS.hotelOrgs.createNewUser(orgId),
        userData
      );
      return response.data;
    } catch (error) {
      throwApiError(error, 'Failed to create new user in organization');
    }
  }

  /**
   * Delete user from organization
   * @param orgId
   * @param userId
   * @returns
   */
  async deleteUserFromOrganization(orgId: string, userId: string) {
    try {
      const response = await apiUtils.delete(
        API_ENDPOINTS.hotelOrgs.deleteUserFromOrg(orgId),
        { data: { user_ids: [userId] } }
      );
      return response.data;
    } catch (error) {
      throwApiError(error, 'Failed to delete user from organization');
    }
  }

  /**
   * Search for users to add to organization
   * @param id - Organization ID
   * @param search - Search term
   * @returns
   */
  async searchUsers(id: string, search: string) {
    try {
      const response = await apiUtils.get(
        API_ENDPOINTS.hotelOrgs.searchUsers(id, search)
      );
      return response.data;
    } catch (error) {
      throwApiError(error, 'Failed to search users');
    }
  }

  /**
   * Link user to organization
   * @param id - Organization ID
   * @param userId - User ID to link
   * @returns
   */
  async linkUserToOrganization(id: string, userId: string) {
    try {
      const response = await apiUtils.post(
        API_ENDPOINTS.hotelOrgs.linkUser(id),
        { user_id: userId }
      );
      return response.data;
    } catch (error) {
      throwApiError(error, 'Failed to link user to organization');
    }
  }

  /**
   * Generate consolidated template for organization
   * @param id - Organization ID
   * @returns Blob response for file download
   */
  async generateConsolidatedTemplate(id: string) {
    try {
      const response = await apiUtils.get(
        API_ENDPOINTS.hotelOrgs.generateTemplate(id),
        { responseType: 'blob' }
      );
      return response;
    } catch (error) {
      throwApiError(error, 'Failed to generate consolidated template');
    }
  }

  async uploadConsolidatedSubmission(id: string, formData: FormData) {
    try {
      const response = await apiUtils.post(
        API_ENDPOINTS.hotelOrgs.uploadConsolidatedSubmission(id),
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    } catch (error) {
      // throwApiError(error, 'Failed to set consolidated submission setting');
      throw error;
    }
  }
}

export const hotelOrgService = new HotelOrgService();
